import React from 'react';
import { t } from 'i18next';
import { useMatch, useParams } from 'react-router-dom';

import { ErrorMessage, NotionBlock, NotionBlockRenderer } from '@Components';
import ChildDatabaseTable from '../../Components/NotionRenderer/ChildDatabaseTable';
import useResources from '../../CustomHooks/useResources';
import DocumentationSkeleton from './Skeletons/DocumentationSkeleton';

const Documentation = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const isDatabaseRoute = useMatch('/documentation/database/:pageId');
  const isDatabase = !!isDatabaseRoute;

  const { notionPageData } = useResources(pageId, isDatabase);

  // Loading state
  if (notionPageData.isLoading) {
    return <DocumentationSkeleton />;
  }

  // Error state
  if (notionPageData.isError) {
    return <ErrorMessage internalMessage={t('Global.ErrorMessage')} />;
  }

  const pageData = notionPageData.data;

  const renderBlocks = (blocks: NotionBlock[]) => {
    if (!blocks || blocks.length === 0) return null;

    const grouped = blocks.reduce((acc, block) => {
      const lastGroup = acc[acc.length - 1];

      // if is a list item and the last group is the same type, append to it
      if (
        (block.type === 'numbered_list_item' || block.type === 'bulleted_list_item') &&
        lastGroup?.type === block.type
      ) {
        lastGroup.items.push(block);
      } else {
        // Create a new group
        acc.push(
          block.type === 'numbered_list_item' || block.type === 'bulleted_list_item'
            ? { type: block.type, items: [block] }
            : { type: 'single', items: [block] },
        );
      }
      return acc;
    }, [] as Array<{ type: string; items: NotionBlock[] }>);

    return (
      <>
        {grouped.map((group, idx) => {
          if (group.type === 'numbered_list_item') {
            return (
              <ol key={group.type + String(idx)} className="list-decimal text-base leading-relaxed mb-3 pl-5">
                {group.items.map((item) => (
                  <NotionBlockRenderer key={item.id} block={item} variant="documentation" />
                ))}
              </ol>
            );
          }
          if (group.type === 'bulleted_list_item') {
            return (
              <ul key={group.type + String(idx)} className="list-disc text-base leading-relaxed mb-3 pl-5">
                {group.items.map((item) => (
                  <NotionBlockRenderer key={item.id} block={item} variant="documentation" />
                ))}
              </ul>
            );
          }
          return <NotionBlockRenderer key={group.items[0].id} block={group.items[0]} variant="documentation" />;
        })}
      </>
    );
  };

  // The resources endpoint can return a "page" shape (mixed blocks) or a
  // "database" shape (single ``child_database`` block) — including when an
  // admin pasted a database id into ``dataRepository`` and the backend's
  // page→database fallback kicked in. Render the standalone DB directly so
  // it can use the full viewport height instead of the embedded 600px cap.
  const isStandaloneDatabase = pageData?.blocks?.length === 1 && pageData.blocks[0]?.type === 'child_database';

  return (
    <div className="w-full bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Title */}
        {pageData?.title && <h1 className="text-5xl font-bold mb-8">{pageData.title}</h1>}

        {/* Render content */}
        {isStandaloneDatabase && pageData?.blocks ? (
          <ChildDatabaseTable block={pageData.blocks[0]} standalone />
        ) : pageData?.blocks && pageData.blocks.length > 0 ? (
          <div>{renderBlocks(pageData.blocks)}</div>
        ) : (
          <div className="text-gray-600 text-center py-12">
            <p className="text-lg">{t('Documentation.EmptyDocumentationPage')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Documentation;
